import { XCircle } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "../../utils";

const badgeVariants = cva(
  "focus:ring-ring inline-flex h-fit items-center text-nowrap rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "rounded-full border-gray-300 bg-white text-gray-700",
        secondary:
          "bg-secondary-50 text-secondary-800 rounded-full border-transparent",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/80 border-transparent shadow",
        outline: "text-foreground",
        orange: "rounded-full border-[#F9DBAF] bg-orange-50 text-orange-800",
        gray: "rounded-full border-gray-200 bg-gray-50 text-gray-700",
        blue: "rounded-full border-[#C7D7FE] bg-[#EEF4FF] text-[#3538CD]",
        "light-blue":
          "rounded-full border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2]",
        "red-orange":
          "rounded-full border-[#F9DBAF] bg-[#FEF6EE] text-[#B93815]",
        pink: "rounded-full border-[#FCCEEE] bg-[#FDF2FA] text-[#C11574]",
        yellow: "rounded-full border-[#FEF0C7] bg-[#FEF0C7] text-[#93370D]",
        purple: "rounded-full border-[#D9D6FE] bg-[#F4F3FF] text-[#5925DC]",
        green: "rounded-full border-[#ABEFC6] bg-[#ECFDF3] text-[#067647]",
        darkGreen: "rounded-full border-[#C7F2E4] bg-[#E9F8F0] text-[#0F4D28]",
        red: "rounded-full border-[#FECDCA] bg-[#FEF3F2] text-[#B42318]",
        "pro-pro":
          "bg-primary-700 rounded-full border-0 px-4 text-sm font-bold text-white",
        "square-yellow":
          "rounded-sm border-[#FEF0C7] bg-[#FEF0C7] text-[#93370D]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

interface DeletableBadgeProps {
  children: React.ReactElement<BadgeProps>;
  onDelete: () => void;
}

export function DeletableBadge({ children, onDelete }: DeletableBadgeProps) {
  return React.cloneElement(
    children,
    {
      ...children.props,
      className: cn(children.props.className, "flex items-center gap-1"),
      onClick: (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        onDelete();
      },
    },
    children.props.children,
    <XCircle size={16} />
  );
}

export { Badge, badgeVariants };
